import {
  ArrowBack as BackIcon,
  Receipt as InvoiceIcon,
  Payment as PaymentIcon,
  LocalShipping as ShippingIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { format } from "date-fns-jalali";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { fetchOrderDetails } from "../../../application/actions/orderActions";

const OrderDetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux State
  const order = useSelector((state) => state.orders.selectedOrder.data);
  const loading = useSelector((state) => state.orders.selectedOrder.loading);
  const error = useSelector((state) => state.orders.selectedOrder.error);

  useEffect(() => {
    if (id) {
      dispatch(fetchOrderDetails(id));
    }
  }, [id, dispatch]);

  const getOrderSteps = () => {
    return [
      { label: "ثبت سفارش", status: "Pending" },
      { label: "در حال پردازش", status: "Processing" },
      { label: "ارسال شده", status: "Shipped" },
      { label: "تحویل داده شده", status: "Delivered" },
    ];
  };

  const getCurrentStep = (status) => {
    const steps = getOrderSteps();
    const index = steps.findIndex((step) => step.status === status);
    return index >= 0 ? index : 0;
  };

  const getOrderStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "warning";
      case "Processing":
        return "info";
      case "Shipped":
        return "primary";
      case "Delivered":
        return "success";
      case "Cancelled":
        return "error";
      default:
        return "default";
    }
  };

  const getOrderStatusLabel = (status) => {
    const labels = {
      Pending: "در انتظار",
      Processing: "در حال پردازش",
      Shipped: "ارسال شده",
      Delivered: "تحویل داده شده",
      Cancelled: "لغو شده",
    };
    return labels[status] || status;
  };

  const handleBackToList = () => {
    navigate("/orders");
  };

  const handleViewInvoice = () => {
    if (order?.invoiceId) {
      navigate(`/invoices/${order.invoiceId}`);
    }
  };

  const handlePayment = () => {
    if (order?.invoiceId) {
      navigate(`/payment/${order.invoiceId}`);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "80vh",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button startIcon={<BackIcon />} onClick={handleBackToList}>
          بازگشت به لیست سفارشات
        </Button>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          سفارش مورد نظر یافت نشد
        </Alert>
        <Button startIcon={<BackIcon />} onClick={handleBackToList}>
          بازگشت به لیست سفارشات
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={handleBackToList}
          sx={{ mb: 2 }}
        >
          بازگشت به لیست سفارشات
        </Button>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h4" gutterBottom>
              جزئیات سفارش #{order.orderNumber}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {order.createdAt &&
                format(new Date(order.createdAt), "yyyy/MM/dd - HH:mm")}
            </Typography>
          </Box>

          <Chip
            label={getOrderStatusLabel(order.status)}
            color={getOrderStatusColor(order.status)}
            sx={{ fontSize: 16, py: 3, px: 2 }}
          />
        </Box>
      </Box>

      {/* Order Timeline */}
      {order.status !== "Cancelled" && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            وضعیت سفارش
          </Typography>
          <Box sx={{ mt: 3 }}>
            <Stepper activeStep={getCurrentStep(order.status)} alternativeLabel>
              {getOrderSteps().map((step) => (
                <Step key={step.status}>
                  <StepLabel>{step.label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Paper>
      )}

      <Grid container spacing={3}>
        {/* Order Items */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              اقلام سفارش
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>محصول</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>تعداد</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>قیمت واحد</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>جمع</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {order.items && order.items.length > 0 ? (
                    order.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body1">
                            {item.productName || item.packageName || "نامشخص"}
                          </Typography>
                          {item.description && (
                            <Typography variant="body2" color="text.secondary">
                              {item.description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body1">
                            {item.quantity}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body1">
                            {item.unitPrice?.toLocaleString("fa-IR")} ریال
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body1" fontWeight="bold">
                            {(item.quantity * item.unitPrice).toLocaleString(
                              "fa-IR"
                            )}{" "}
                            ریال
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography color="text.secondary">
                          اطلاعات اقلام در دسترس نیست
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Order Summary */}
            <Box sx={{ mt: 3, pt: 2, borderTop: 2, borderColor: "divider" }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body1">جمع کل:</Typography>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: "right" }}>
                  <Typography variant="body1">
                    {order.subtotal?.toLocaleString("fa-IR") ||
                      order.totalAmount?.toLocaleString("fa-IR")}{" "}
                    ریال
                  </Typography>
                </Grid>

                {order.discount > 0 && (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="body1" color="error">
                        تخفیف:
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: "right" }}>
                      <Typography variant="body1" color="error">
                        -{order.discount?.toLocaleString("fa-IR")} ریال
                      </Typography>
                    </Grid>
                  </>
                )}

                {order.tax > 0 && (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="body1">مالیات:</Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: "right" }}>
                      <Typography variant="body1">
                        {order.tax?.toLocaleString("fa-IR")} ریال
                      </Typography>
                    </Grid>
                  </>
                )}

                <Grid item xs={6}>
                  <Typography variant="h6" fontWeight="bold">
                    مبلغ کل:
                  </Typography>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: "right" }}>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    {order.totalAmount?.toLocaleString("fa-IR")} ریال
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>

        {/* Order Info Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Customer Info */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              اطلاعات مشتری
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                نام:
              </Typography>
              <Typography variant="body1">
                {order.customerName || "نامشخص"}
              </Typography>
            </Box>

            {order.customerPhone && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  تلفن:
                </Typography>
                <Typography variant="body1">{order.customerPhone}</Typography>
              </Box>
            )}

            {order.customerEmail && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  ایمیل:
                </Typography>
                <Typography variant="body1">{order.customerEmail}</Typography>
              </Box>
            )}
          </Paper>

          {/* Delivery Info */}
          {order.deliveryAddress && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                اطلاعات تحویل
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  آدرس:
                </Typography>
                <Typography variant="body1">{order.deliveryAddress}</Typography>
              </Box>

              {order.expectedDeliveryDate && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    تاریخ تحویل پیش‌بینی شده:
                  </Typography>
                  <Typography variant="body1">
                    {format(new Date(order.expectedDeliveryDate), "yyyy/MM/dd")}
                  </Typography>
                </Box>
              )}

              {order.actualDeliveryDate && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    تاریخ تحویل واقعی:
                  </Typography>
                  <Typography variant="body1" color="success.main">
                    {format(new Date(order.actualDeliveryDate), "yyyy/MM/dd")}
                  </Typography>
                </Box>
              )}
            </Paper>
          )}

          {/* Actions */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              عملیات
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              {order.invoiceId && (
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<InvoiceIcon />}
                    onClick={handleViewInvoice}
                  >
                    مشاهده فاکتور
                  </Button>
                </Grid>
              )}

              {order.status === "Pending" && !order.isPaid && (
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<PaymentIcon />}
                    onClick={handlePayment}
                  >
                    پرداخت سفارش
                  </Button>
                </Grid>
              )}

              {order.trackingNumber && (
                <Grid item xs={12}>
                  <Alert severity="info" icon={<ShippingIcon />}>
                    <Typography variant="body2">
                      <strong>کد رهگیری:</strong> {order.trackingNumber}
                    </Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Notes */}
          {order.notes && (
            <Paper sx={{ p: 3, mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                یادداشت‌ها
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                {order.notes}
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default OrderDetails;
